import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import * as moment from 'moment-timezone';

import { StaffSiteSale, StaffSiteSaleLineItem } from '../../../../../../../models/staff-site-sale';
import { Membership, MembershipPerCustomerAddOn } from '../../../../../../../models/membership';
import { LineItemPurchaseType } from '../../../../../../../models/payment-enums';
import { StaffSiteSaleService } from '../../../../../../../services/staff-site-sale/staff-site-sale.service';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../utils';
import { SelectCustomerModalComponent } from '../../../../modals/select-customer-modal/select-customer-modal.component';
import { Customer, CustomerSearchResult } from '../../../../../../../models/customer';
import {
  CreateNewCustomerModalComponent
} from '../../../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';
import {
  OrganizationConfigService
} from '../../../../../../../services/organization-config/organization-config.service';
import { LocationService } from '../../../../../../../services/location/location.service';
import { AccountService } from '../../../../../../../services/account/account.service';


@Component({
  selector: 'app-add-membership-line-item-to-sale-modal',
  templateUrl: './add-membership-line-item-to-sale-modal.component.html',
  styleUrls: ['./add-membership-line-item-to-sale-modal.component.css']
})
export class AddMembershipLineItemToSaleModalComponent implements OnInit {
  @Input() sale: StaffSiteSale;
  @Input() memberships: Membership[];
  @Input() lineItemToCopy: StaffSiteSaleLineItem | null = null;
  @Input() replaceExistingLineItem = false;
  @Input() savedCallback: (updatedSale: StaffSiteSale) => undefined;

  public lineItem: StaffSiteSaleLineItem = {
    id: null,
    purchase_type: LineItemPurchaseType.membershipInitial,
    description: '',
    product_type_id: null,
    product_id: null,
    product_location_id: null,
    product_location_name: null,
    product_start_date: null,
    product_end_date: null,
    customer_id: null,
    customer_first_name: null,
    customer_last_name: null,
    customer_birthday: null,
    customer_email: null,
    customer_profile_image_url: null,
    customer_is_checked_in: false,
    amount_pre_tax: null,
    tax_amount: null,
    discount_amount: null,
    amount: null,
    deposit_amount: null,
    terms_and_conditions_checkbox_text: '',
    add_on_ids: [],
    add_on_names: [],
    number_of_uses_remaining: null,
    customers_checked_in_using_product: [],
    lightspeed_product_id: '',
    discounts: [],
    order_number: null,
  };

  public minStartDate: string;

  public loadingSubmit = false;
  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  protected readonly propExists = propExists;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    public locationService: LocationService,
    private modalService: NgbModal,
    private staffSiteSaleService: StaffSiteSaleService,
    private accountService: AccountService,
  ) {}

  ngOnInit() {
    this.minStartDate = getISO8601DateStringFromMoment(
      moment().tz(this.orgConfigService.getOrganizationConfig().time_zone)
    )

    if (propExists(this.lineItemToCopy)) {
      this.lineItem.product_type_id = this.lineItemToCopy.product_type_id;
      this.lineItem.product_location_id = this.lineItemToCopy.product_location_id;
      this.lineItem.product_start_date = this.lineItemToCopy.product_start_date;
      this.lineItem.customer_id = this.lineItemToCopy.customer_id;
      this.lineItem.customer_first_name = this.lineItemToCopy.customer_first_name;
      this.lineItem.customer_last_name = this.lineItemToCopy.customer_last_name;
      this.lineItem.customer_birthday = this.lineItemToCopy.customer_birthday;
      this.lineItem.customer_email = this.lineItemToCopy.customer_email;
      this.lineItem.add_on_ids = this.lineItemToCopy.add_on_ids;
      if (this.getSelectedMembership() && this.getSelectedMembership().has_fixed_date_ranges) {
        for (const dro of this.getSelectedMembershipFilteredDateRanges()) {
          if (dro.start_date === this.lineItem.product_start_date) {
            this.lineItem.product_date_range = dro.start_date + ':' + dro.end_date;
          }
        }
      }
      if (this.replaceExistingLineItem) {
        this.lineItem.order_number = this.lineItemToCopy.order_number;
      }
    } else {
      this.lineItem.customer_id = this.sale.customer_id;
      this.lineItem.product_start_date = this.minStartDate;
      this.lineItem.customer_first_name = this.sale.customer_first_name;
      this.lineItem.customer_last_name = this.sale.customer_last_name;
      this.lineItem.customer_birthday = this.sale.customer_birthday;
      this.lineItem.customer_email = this.sale.customer_email;
    }
  }

  public purchasingCustomerIsAssigned() {
    return (this.lineItem.customer_id === this.sale.customer_id);
  }

  public assignPurchasingCustomer() {
    this.lineItem.customer_id = this.sale.customer_id;
    this.lineItem.customer_first_name = this.sale.customer_first_name;
    this.lineItem.customer_last_name = this.sale.customer_last_name;
    this.lineItem.customer_birthday = this.sale.customer_birthday;
    this.lineItem.customer_email = this.sale.customer_email;
  }

  public openSelectCustomerModal() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      this.lineItem.customer_id = result.id;
      this.lineItem.customer_first_name = result.first_name;
      this.lineItem.customer_last_name = result.last_name;
      this.lineItem.customer_birthday = result.birthday;
      this.lineItem.customer_email = result.email;
    };
  }

  public openCreateCustomerModal() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.lineItem.customer_id = result.id;
      this.lineItem.customer_first_name = result.first_name;
      this.lineItem.customer_last_name = result.last_name;
      this.lineItem.customer_birthday = result.birthday;
      this.lineItem.customer_email = result.email;
    };
  }

  public getSelectedMembership(): Membership {
    if (!this.lineItem.product_type_id) { return null; }

    for (const membership of this.memberships) {
      if (membership.id === this.lineItem.product_type_id) {
        return membership;
      }
    }

    return null;
  }

  public getSelectedMembershipAddOns(): MembershipPerCustomerAddOn[] {
    if (!this.lineItem.product_type_id) { return []; }

    for (const membership of this.memberships) {
      if (membership.id === this.lineItem.product_type_id) {
        return membership.per_customer_add_ons;
      }
    }

    return [];
  }

  public addOnIsSelected(addOnId: string): boolean {
    return this.lineItem.add_on_ids.includes(addOnId);
  }

  public toggleAddOn(addOnId: string) {
    if (this.addOnIsSelected(addOnId)) {
      this.lineItem.add_on_ids.splice(
        this.lineItem.add_on_ids.indexOf(addOnId),
        1
      );
    } else {
      this.lineItem.add_on_ids.push(addOnId);
    }
  }

  public allFieldsEntered(): boolean {
    return (
      propExists(this.lineItem.product_type_id) &&
      propExists(this.lineItem.customer_id)
    );
  }

  public handleSubmit() {
    if (!this.allFieldsEntered()) {
      return;
    }

    this.loadingSubmit = true;

    this.staffSiteSaleService
      .createLineItem(this.sale.id, this.lineItem)
      .then(saleWithCreated => {
        if (this.replaceExistingLineItem) {
          return this.staffSiteSaleService
            .removeLineItem(
              this.sale.id,
              this.lineItemToCopy.id,
            )
            .then(saleWithRemoval => {
              this.savedCallback(saleWithRemoval);
              this.activeModal.close();
            });
        } else {
          this.savedCallback(saleWithCreated);
          this.activeModal.close();
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleMembershipTypeChange() {
    const membership = this.getSelectedMembership();
    if (membership.is_single_location && !propExists(this.lineItem.product_location_id)) {
      this.lineItem.product_location_id = this.accountService.getMyAccount().default_gym_id;
    }
    if (!membership.is_single_location && propExists(this.lineItem.product_location_id)) {
      this.lineItem.product_location_id = null;
    }
  }

  public getSelectedMembershipFilteredDateRanges() {
    const selectedMembership = this.getSelectedMembership();
    if (!propExists(selectedMembership)) { return null; }
    if (!selectedMembership.has_fixed_date_ranges) { return null; }

    return selectedMembership.date_range_options
      .filter(dro => {
        return (dro.end_date >= this.minStartDate);
      })
      .sort((a, b) => {
        return (a.start_date > b.start_date) ? 1 : -1;
      })
  }

}
